import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { queryOptions } from "@tanstack/react-query";
import { lazy, Suspense, useEffect, useRef, useState } from "react";

import { AnnouncementBar } from "@/components/AnnouncementBar";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { getPublishedProduct } from "@/lib/admin/products.functions";
import { ChevronLeft, Truck, ShieldCheck, ChevronRight, Star, Package, RotateCcw, Award, Maximize2 } from "lucide-react";
import { RETURNS_DAYS_DEFAULT } from "@/lib/site-config";
import { FREE_SHIPPING_OVER_CENTS_FALLBACK } from "@/lib/site-config";
import { AddToCartButton } from "@/components/cart/AddToCartButton";
import { StockBadge } from "@/components/product/StockBadge";
import { useTrackRecentlyViewed } from "@/hooks/useRecentlyViewed";
import { trackProductView } from "@/lib/analytics/tracker";
import { MODEL_MAP, type ModelId } from "@/lib/catalog";

// Below-the-fold — code-split to shrink initial JS and improve TBT/LCP.
const ProductGalleryLightbox = lazy(() =>
  import("@/components/product/ProductGalleryLightbox").then((m) => ({ default: m.ProductGalleryLightbox })),
);
const ShareButton = lazy(() =>
  import("@/components/product/ShareButton").then((m) => ({ default: m.ShareButton })),
);
const ExpressCheckout = lazy(() =>
  import("@/components/checkout/ExpressCheckout").then((m) => ({ default: m.ExpressCheckout })),
);
const StickyAddToCartBar = lazy(() =>
  import("@/components/product/StickyAddToCartBar").then((m) => ({ default: m.StickyAddToCartBar })),
);
const HistoryRail = lazy(() =>
  import("@/components/product/HistoryRail").then((m) => ({ default: m.HistoryRail })),
);
const RelatedRail = lazy(() =>
  import("@/components/product/RelatedRail").then((m) => ({ default: m.RelatedRail })),
);
const ReviewsSection = lazy(() =>
  import("@/components/product/ReviewsSection").then((m) => ({ default: m.ReviewsSection })),
);


const productQueryOptions = (slug: string) =>
  queryOptions({
    queryKey: ["product", slug],
    queryFn: () => getPublishedProduct({ data: { slug } }),
    staleTime: 5 * 60_000,
  });

export const Route = createFileRoute("/p/$slug")({
  loader: async ({ params, context }) => {
    const data = await context.queryClient.ensureQueryData(productQueryOptions(params.slug));
    if (!data) throw notFound();
    return data;
  },
  head: ({ loaderData }) => {
    const p = loaderData?.product;
    if (!p) return { meta: [{ title: "Product not found | FIXHEN" }] };
    const url = `https://fixhen.com/p/${p.slug}`;
    const images = loaderData?.images ?? [];
    const firstImg = images?.[0];
    const image = firstImg?.signed_url;
    const imageAbs = image
      ? image.startsWith("http")
        ? image
        : `https://fixhen.com${image.startsWith("/") ? image : `/${image}`}`
      : undefined;
    // Facebook/LinkedIn only render a preview thumbnail when they can size the
    // image up front. Serve a dedicated, fixed 1200x1200 JPEG variant and
    // declare its type + dimensions so the crawler doesn't have to fetch and
    // measure it asynchronously (which shows a text-only card on first paste).
    const ogImage = (() => {
      if (!imageAbs) return "https://fixhen.com/og/shop.jpg";
      try {
        const u = new URL(imageAbs);
        if (u.pathname.startsWith("/api/public/img/")) {
          u.search = "";
          u.searchParams.set("w", "1200");
          u.searchParams.set("h", "1200");
          u.searchParams.set("q", "85");
          return u.toString();
        }
        return imageAbs;
      } catch {
        return imageAbs;
      }
    })();

    const variants = loaderData?.variants ?? [];
    const faqs: Array<{ q: string; a: string }> = p.faqs ?? [];
    const availMap: Record<string, string> = {
      in_stock: "https://schema.org/InStock",
      out_of_stock: "https://schema.org/OutOfStock",
      preorder: "https://schema.org/PreOrder",
      backorder: "https://schema.org/BackOrder",
    };
    const condMap: Record<string, string> = {
      new: "https://schema.org/NewCondition",
      refurbished: "https://schema.org/RefurbishedCondition",
      used: "https://schema.org/UsedCondition",
    };
    const toAbs = (u?: string | null) =>
      !u ? null : u.startsWith("http") ? u : `https://fixhen.com${u.startsWith("/") ? u : `/${u}`}`;
    const priceValidUntil = new Date(Date.now() + 365 * 864e5).toISOString().slice(0, 10);
    const offers = variants.length
      ? variants.map((v: any) => ({
          "@type": "Offer",
          name: v.label,
          sku: `${p.sku}${v.sku_suffix ?? ""}`,
          priceCurrency: p.currency || "EUR",
          price: ((v.price_cents > 0 ? v.price_cents : p.price_cents) / 100).toFixed(2),
          priceValidUntil,
          availability: availMap[p.availability ?? "in_stock"],
          itemCondition: condMap[p.condition ?? "new"],
          url,
        }))
      : [
          {
            "@type": "Offer",
            priceCurrency: p.currency || "EUR",
            price: (p.price_cents / 100).toFixed(2),
            priceValidUntil,
            availability: availMap[p.availability ?? "in_stock"],
            itemCondition: condMap[p.condition ?? "new"],
            url,
          },
        ];
    const productLd: any = {
      "@context": "https://schema.org/",
      "@type": "Product",
      name: p.title,
      description: (p.meta_description || p.description || "").replace(/<[^>]+>/g, "").slice(0, 300),
      sku: p.sku,
      brand: { "@type": "Brand", name: p.brand },
      image: images.map((i: any) => toAbs(i.signed_url)).filter(Boolean),
      offers: offers.length === 1 ? offers[0] : { "@type": "AggregateOffer", priceCurrency: p.currency || "EUR", lowPrice: (Math.min(...offers.map((o: any) => parseFloat(o.price))) ).toFixed(2), highPrice: (Math.max(...offers.map((o: any) => parseFloat(o.price))) ).toFixed(2), offerCount: offers.length, offers },
    };
    // Shipping + returns policy embedded on every offer (Merchant listings rich result)
    const returnDays = p.return_days || RETURNS_DAYS_DEFAULT;
    const shippingDetails = {
      "@type": "OfferShippingDetails",
      shippingRate: { "@type": "MonetaryAmount", value: "0", currency: "EUR" },
      shippingDestination: [
        { "@type": "DefinedRegion", addressCountry: "BG" },
        { "@type": "DefinedRegion", addressCountry: "EU" },
      ],
      deliveryTime: {
        "@type": "ShippingDeliveryTime",
        handlingTime: { "@type": "QuantitativeValue", minValue: 0, maxValue: 2, unitCode: "DAY" },
        transitTime: { "@type": "QuantitativeValue", minValue: 1, maxValue: 14, unitCode: "DAY" },
      },
    };
    const returnPolicy = {
      "@type": "MerchantReturnPolicy",
      applicableCountry: "EU",
      returnPolicyCategory: "https://schema.org/MerchantReturnFiniteReturnWindow",
      merchantReturnDays: returnDays,
      returnMethod: "https://schema.org/ReturnByMail",
      returnFees: "https://schema.org/FreeReturn",
    };
    const enrichOffer = (o: any) => ({ ...o, shippingDetails, hasMerchantReturnPolicy: returnPolicy });
    if (productLd.offers["@type"] === "Offer") {
      productLd.offers = enrichOffer(productLd.offers);
    } else {
      productLd.offers.offers = productLd.offers.offers.map(enrichOffer);
    }
    if (p.gtin) productLd.gtin = p.gtin;
    if (p.mpn) productLd.mpn = p.mpn;
    if (p.material) productLd.material = p.material;
    if (p.weight_grams) productLd.weight = { "@type": "QuantitativeValue", value: p.weight_grams, unitCode: "GRM" };
    if (p.country_of_origin) productLd.countryOfOrigin = p.country_of_origin;
    const topReviews = (loaderData as any)?.topReviews ?? [];
    // Only real-customer reviews feed structured data (AI-seeded ones are
    // display-only), so the aggregate comes from the organic subset.
    const organicRating = (loaderData as any)?.organicRating ?? null;
    if (organicRating?.count) {
      productLd.aggregateRating = {
        "@type": "AggregateRating",
        ratingValue: organicRating.value,
        reviewCount: organicRating.count,
        bestRating: 5,
        worstRating: 1,
      };
    }
    if (topReviews.length) {
      productLd.review = topReviews.map((r: any) => ({
        "@type": "Review",
        author: { "@type": "Person", name: r.author || "Customer" },
        datePublished: r.date,
        name: r.title,
        reviewBody: r.body,
        reviewRating: {
          "@type": "Rating",
          ratingValue: r.rating,
          bestRating: 5,
          worstRating: 1,
        },
      }));
    }
    if (p.oem_numbers?.length) {
      productLd.identifier = p.oem_numbers.map((v: string) => ({
        "@type": "PropertyValue",
        propertyID: "OEM",
        value: v,
      }));
    }
    const breadcrumbLd = {
      "@context": "https://schema.org/",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Shop", item: "https://fixhen.com/shop" },
        ...(p.brand ? [{ "@type": "ListItem", position: 2, name: p.brand, item: `https://fixhen.com/shop?brand=${encodeURIComponent(p.brand)}` }] : []),
        { "@type": "ListItem", position: p.brand ? 3 : 2, name: p.title, item: url },
      ],
    };
    const scripts: any[] = [
      { type: "application/ld+json", children: JSON.stringify(productLd) },
      { type: "application/ld+json", children: JSON.stringify(breadcrumbLd) },
    ];
    if (faqs.length) {
      scripts.push({
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org/",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      });
    }
    const plainDesc = (p.description || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
    const metaDesc =
      p.meta_description ||
      (plainDesc ? plainDesc.slice(0, 155) : `${p.title}${p.brand ? ` by ${p.brand}` : ""} — genuine and quality-matched parts from FIXHEN, shipped worldwide.`);
    return {
      meta: [
        { title: `${p.meta_title || p.title} | FIXHEN` },
        { name: "description", content: metaDesc },
        { property: "og:title", content: p.meta_title || p.title },
        { property: "og:description", content: metaDesc },

        { property: "og:image", content: ogImage },
        { property: "og:image:secure_url", content: ogImage },
        { property: "og:image:type", content: "image/jpeg" },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "1200" },
        { property: "og:image:alt", content: p.title },
        { name: "twitter:image", content: ogImage },
        { name: "twitter:card", content: "summary_large_image" },

        { property: "og:type", content: "product" },
        { property: "og:url", content: url },
        { property: "product:price:amount", content: (p.price_cents / 100).toFixed(2) },
        { property: "product:price:currency", content: p.currency || "EUR" },
        { property: "product:availability", content: p.availability ?? "in_stock" },
        { property: "product:condition", content: p.condition ?? "new" },
        { property: "product:brand", content: p.brand || "" },
        ...(p.mpn ? [{ property: "product:retailer_item_id", content: p.mpn }] : []),
        ...(p.gtin ? [{ property: "product:gtin", content: p.gtin } as any] : []),
      ],
      links: [
        { rel: "canonical", href: url },
        ...(image
          ? [
              {
                rel: "preload",
                as: "image",
                href: image,
                fetchPriority: "high",
              } as any,
            ]
          : []),
      ],
      scripts,
    };
  },
  component: ProductPage,
});

function ProductPage() {
  const data = Route.useLoaderData();
  const { product, images, variants } = data!;
  const [active, setActive] = useState(0);
  const [variantId, setVariantId] = useState<string | null>(variants?.[0]?.id ?? null);
  const [qty, setQty] = useState(1);
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [deferredReady, setDeferredReady] = useState(false);
  const mountedRef = useRef(false);
  const addToCartRef = useRef<HTMLDivElement | null>(null);
  const currentVar = variants.find((v: any) => v.id === variantId);
  const price = (currentVar?.price_cents && currentVar.price_cents > 0 ? currentVar.price_cents : product.price_cents) / 100;
  const unitCents = currentVar?.price_cents && currentVar.price_cents > 0 ? currentVar.price_cents : product.price_cents;
  const faqs: Array<{ q: string; a: string }> = product.faqs ?? [];
  const oems: string[] = product.oem_numbers ?? [];
  const specs: Array<[string, string]> = [
    product.brand && ["Brand", product.brand],
    product.material && ["Material", product.material],
    product.weight_grams && ["Weight", `${product.weight_grams} g`],
    product.condition && ["Condition", product.condition.replace("_", " ")],
    product.country_of_origin && ["Origin", product.country_of_origin],
    product.warranty_months && ["Warranty", `${product.warranty_months} months`],
    product.gtin && ["GTIN", product.gtin],
    product.mpn && ["MPN", product.mpn],
  ].filter(Boolean) as Array<[string, string]>;

  useTrackRecentlyViewed(product.id);

  useEffect(() => {
    trackProductView({
      sku: product.sku,
      slug: product.slug,
      title: product.title,
      priceEur: product.price_cents ? product.price_cents / 100 : null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product.id]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (deferredReady) return;
    const reveal = () => {
      if (mountedRef.current) setDeferredReady(true);
    };
    const timeout = window.setTimeout(reveal, 2600);
    return () => {
      window.clearTimeout(timeout);
    };
  }, [deferredReady]);

  const firstModel: string | undefined = Array.isArray(product.model_ids) ? product.model_ids[0] : undefined;
  const modelLabel = firstModel ? (MODEL_MAP[firstModel as ModelId]?.label ?? String(firstModel).toUpperCase()) : null;
  const shareUrl = typeof window !== "undefined"
    ? window.location.href
    : `https://fixhen.com/p/${product.slug}`;

  return (
    <div>
      <AnnouncementBar />
      <SiteHeader />
      <main className="max-w-6xl mx-auto px-4 py-6">
        <nav aria-label="Breadcrumb" className="mb-3 text-xs text-slate-500 flex items-center gap-1 flex-wrap">
          <Link to={"/shop" as any} className="hover:text-[#2563EB]">Shop</Link>
          {product.brand && (
            <>
              <ChevronRight className="h-3 w-3" />
              <Link
                to={"/shop" as any}
                search={{ brand: product.brand.toLowerCase() } as any}
                aria-label={`Shop all ${product.brand} parts`}
                className="hover:text-[#2563EB]"
              >
                {product.brand}
              </Link>
            </>
          )}
          <ChevronRight className="h-3 w-3" />
          <span className="text-slate-700 font-medium truncate">{product.title}</span>
        </nav>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <div className="group relative aspect-square rounded-xl border bg-white overflow-hidden">
              {images[active]?.signed_url && (
                <>
                  <img
                    src={images[active].signed_url_sm ?? images[active].signed_url}
                    srcSet={
                      images[active].signed_url && images[active].signed_url_sm
                        ? `${images[active].signed_url_sm} 400w, ${images[active].signed_url} 800w`
                        : undefined
                    }
                    sizes="(max-width: 768px) 100vw, 600px"
                    alt={images[active].alt_text}
                    fetchPriority="high"
                    loading="eager"
                    decoding="sync"
                    width={800}
                    height={800}
                    className="absolute inset-0 w-full h-full object-contain p-4"
                  />
                  <button
                    type="button"
                    onClick={() => setLightboxOpen(true)}
                    aria-label="Open image in full view"
                    className="absolute inset-0 cursor-zoom-in bg-transparent"
                  />
                </>
              )}
              {images[active]?.signed_url ? (
                <div className="pointer-events-none absolute inset-0 flex items-start justify-between p-2.5 opacity-0 transition-opacity group-hover:opacity-100">
                  <span className="pointer-events-auto inline-flex items-center gap-1 rounded-full bg-white/90 backdrop-blur px-2.5 py-1 text-[11px] font-semibold text-[#172554] shadow-sm">
                    <Maximize2 className="h-3 w-3" /> Zoom
                  </span>
                  <span className="pointer-events-auto">
                    {deferredReady ? (
                      <Suspense fallback={null}>
                        <ShareButton url={shareUrl} title={product.title} />
                      </Suspense>
                    ) : null}
                  </span>
                </div>
              ) : null}
            </div>
            <div className="mt-2 grid grid-cols-6 gap-1.5">
              {images.map((im: any, i: number) => (
                <button
                  key={im.id}
                  onClick={() => setActive(i)}
                  aria-label={`View image ${i + 1}`}
                  className={`aspect-square rounded border-2 overflow-hidden bg-white ${i === active ? "border-[#2563EB]" : "border-slate-200"}`}
                >
                  {deferredReady && (im.signed_url_thumb ?? im.signed_url) && (
                    <img
                      src={im.signed_url_thumb ?? im.signed_url}
                      alt=""
                      loading="lazy"
                      decoding="async"
                      fetchPriority="low"
                      width={160}
                      height={160}
                      className="w-full h-full object-contain p-1"
                    />
                  )}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            <h1 className="text-2xl font-extrabold text-[#172554]">{product.title}</h1>
            {product.rating_value && product.rating_count ? (
              <div className="flex items-center gap-2 text-sm">
                <span className="flex items-center text-amber-500">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} className="h-4 w-4" fill={i < Math.round(product.rating_value) ? "currentColor" : "none"} />
                  ))}
                </span>
                <span className="font-semibold">{product.rating_value.toFixed(1)}</span>
                <span className="text-slate-500">({product.rating_count} reviews)</span>
              </div>
            ) : null}
            <div className="flex items-end gap-3">
              <div className="text-3xl font-bold text-[#172554]">€{price.toFixed(2)}</div>
              <div className="text-xs text-slate-500 mb-1">
                VAT incl. ·{" "}
                {product.availability === "in_stock" ? (
                  <StockBadge />
                ) : (
                  product.availability?.replace("_", " ")
                )}
              </div>
            </div>

            {variants.length > 0 && (
              <div className="space-y-1">
                <div className="text-xs font-semibold uppercase text-slate-500">Choose variant</div>
                <div className="flex flex-wrap gap-2">
                  {variants.map((v: any) => (
                    <button
                      key={v.id}
                      onClick={() => setVariantId(v.id)}
                      className={`px-3 py-1.5 text-xs rounded-md border ${
                        variantId === v.id ? "border-[#2563EB] bg-blue-50 font-semibold text-[#172554]" : "text-slate-600"
                      }`}
                    >
                      {v.label}
                      {v.price_cents > 0 && v.price_cents !== product.price_cents && (
                        <span className="ml-1 text-[10px] text-slate-500">€{(v.price_cents / 100).toFixed(2)}</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div ref={addToCartRef} className="space-y-2">
              <AddToCartButton
                onQtyChange={setQty}
                productId={product.id}
                variantId={currentVar?.id ?? null}
                sku={`${product.sku}${currentVar?.sku_suffix ?? ""}`}
                slug={product.slug}
                title={product.title}
                brand={product.brand}
                variantLabel={currentVar?.label ?? null}
                image={images[0]?.signed_url ?? null}
                unitPriceCents={unitCents}
                currency={product.currency || "EUR"}
                disabled={product.availability === "out_of_stock"}
                disabledLabel={product.availability === "out_of_stock" ? "Out of stock" : undefined}
              />

              {product.availability !== "out_of_stock" && (
                <Suspense fallback={null}>
                  <ExpressCheckout
                    items={[
                      {
                        productId: product.id,
                        variantId: currentVar?.id ?? null,
                        qty,
                        title: product.title,
                        unitPriceCents: unitCents,
                      },
                    ]}
                    subtotalCents={unitCents * qty}
                    currency={(product.currency || "EUR").toLowerCase()}
                  />
                </Suspense>
              )}
            </div>

            <TrustBadges productSku={product.sku} productMpn={product.mpn} warrantyMonths={product.warranty_months} returnDays={product.return_days || RETURNS_DAYS_DEFAULT} />

            {oems.length > 0 && (
              <div className="border-t pt-3">
                <div className="text-xs font-semibold uppercase text-slate-500 mb-1.5">OEM compatibility</div>
                <div className="flex flex-wrap gap-1">
                  {oems.map((o) => (
                    <span key={o} className="text-[11px] font-mono px-1.5 py-0.5 rounded bg-slate-100 text-[#172554] border">{o}</span>
                  ))}
                </div>
              </div>
            )}

            <div
              className="prose prose-sm product-description max-w-none border-t pt-4"
              dangerouslySetInnerHTML={{
                __html: product.description_html ?? "",
              }}
            />

            {specs.length > 0 && (
              <div className="border-t pt-4">
                <h2 className="text-sm font-bold text-[#172554] mb-2">Specifications</h2>
                <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                  {specs.map(([k, v]) => (
                    <div key={k} className="flex justify-between border-b border-slate-100 py-1">
                      <dt className="text-slate-500">{k}</dt>
                      <dd className="font-semibold text-slate-700">{v}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}
          </div>
        </div>

        {faqs.length > 0 && (
          <section className="mt-10 max-w-3xl mx-auto" itemScope itemType="https://schema.org/FAQPage">
            <h2 className="text-xl font-bold text-[#172554] mb-3">Frequently asked questions</h2>
            <div className="space-y-2">
              {faqs.map((f, i) => {
                const open = openFaq === i;
                return (
                  <div key={i} itemScope itemProp="mainEntity" itemType="https://schema.org/Question" className="rounded-lg border bg-white overflow-hidden">
                    <button
                      onClick={() => setOpenFaq(open ? null : i)}
                      className="w-full text-left px-4 py-3 flex items-center justify-between gap-3 hover:bg-slate-50"
                    >
                      <span itemProp="name" className="font-semibold text-sm text-[#172554]">{f.q}</span>
                      <ChevronRight className={`h-4 w-4 text-slate-400 transition-transform ${open ? "rotate-90" : ""}`} />
                    </button>
                    {open && (
                      <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer" className="px-4 pb-3 -mt-1">
                        <p itemProp="text" className="text-sm text-slate-600 leading-relaxed whitespace-pre-line">{f.a}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {deferredReady ? (
          <Suspense fallback={<div style={{ contentVisibility: "auto", containIntrinsicSize: "1px 600px" }} />}>
            <ReviewsSection
              productId={product.id}
              productTitle={product.title}
              ratingValue={Number(product.rating_value ?? 0)}
              ratingCount={product.rating_count ?? 0}
            />
          </Suspense>
        ) : (
          <div style={{ contentVisibility: "auto", containIntrinsicSize: "1px 600px" }} />
        )}

        {deferredReady ? (
          <Suspense fallback={<div style={{ contentVisibility: "auto", containIntrinsicSize: "1px 400px" }} />}>
            <RelatedRail
              productId={product.id}
              modelLabel={modelLabel}
              modelIds={Array.isArray(product.model_ids) ? product.model_ids : []}
              ctaHref={
                firstModel
                  ? `/shop?${product.brand ? `brand=${product.brand.toLowerCase()}&` : ""}models=${firstModel}`
                  : "/shop"
              }
            />
          </Suspense>
        ) : (
          <div style={{ contentVisibility: "auto", containIntrinsicSize: "1px 400px" }} />
        )}
        {deferredReady ? (
          <Suspense fallback={<div style={{ contentVisibility: "auto", containIntrinsicSize: "1px 300px" }} />}>
            <HistoryRail excludeProductId={product.id} />
          </Suspense>
        ) : (
          <div style={{ contentVisibility: "auto", containIntrinsicSize: "1px 300px" }} />
        )}
      </main>
      <SiteFooter />

      {lightboxOpen && (
        <Suspense fallback={null}>
          <ProductGalleryLightbox
            open={lightboxOpen}
            onOpenChange={setLightboxOpen}
            images={images as any}
            index={active}
            onIndexChange={setActive}
            title={product.title}
          />
        </Suspense>
      )}


      {deferredReady ? (
        <Suspense fallback={null}>
          <StickyAddToCartBar
            productId={product.id}
            variantId={currentVar?.id ?? null}
            sku={`${product.sku}${currentVar?.sku_suffix ?? ""}`}
            slug={product.slug}
            title={product.title}
            brand={product.brand}
            variantLabel={currentVar?.label ?? null}
            image={images[0]?.signed_url ?? null}
            unitPriceCents={unitCents}
            currency={product.currency || "EUR"}
            disabled={product.availability === "out_of_stock"}
            observeRef={addToCartRef}
          />
        </Suspense>
      ) : null}
    </div>
  );
}

function Badge({ icon, label, sub }: { icon: React.ReactNode; label: string; sub: string }) {
  return (
    <div className="flex items-start gap-2 rounded-md border bg-white px-2.5 py-2">
      <div className="text-[#2563EB] mt-0.5">{icon}</div>
      <div className="min-w-0">
        <div className="font-semibold text-[12px] text-slate-700 truncate">{label}</div>
        <div className="text-[10px] text-slate-600 truncate">{sub}</div>
      </div>
    </div>
  );
}

function TrustBadges({
  productSku,
  productMpn,
  warrantyMonths,
  returnDays,
}: {
  productSku: string;
  productMpn?: string | null;
  warrantyMonths?: number | null;
  returnDays: number;
}) {
  const shipSub = `Free over €${(FREE_SHIPPING_OVER_CENTS_FALLBACK / 100).toFixed(0)}`;
  return (
    <div className="grid grid-cols-2 gap-2 text-[11px] text-slate-600">
      <Badge icon={<Truck className="h-3.5 w-3.5" />} label="Worldwide shipping" sub={shipSub} />
      <Badge icon={<RotateCcw className="h-3.5 w-3.5" />} label={`${returnDays}-day returns`} sub="No questions asked" />
      {warrantyMonths ? <Badge icon={<Award className="h-3.5 w-3.5" />} label={`${warrantyMonths}-month warranty`} sub="Manufacturer-backed" /> : null}
      <Badge icon={<Package className="h-3.5 w-3.5" />} label={`SKU ${productSku}`} sub={productMpn ? `MPN ${productMpn}` : "FIXHEN"} />
    </div>
  );
}
